/**
 * Config of the extranet login page: texts, error messages, the login
 * submission (fetch) and the two `EntityForm` configs. Kept out of
 * `LoginPage.tsx` so the page module exports components only.
 */

import type { EntityFormConfig } from '@components/integrated/entity-form';

import type { ErpSession, ExtranetEnvironmentsPayload } from '../auth/types';

/** Login endpoint, same-origin like claimflow's login form. */
export const LOGIN_ENDPOINT = '/api/extranet/auth/login';

/** Page texts. */
export const LOGIN_TEXT = {
  title: 'Connexion',
  subtitle: 'Accédez à votre espace client professionnel.',
  sessionTitle: 'Choix de la session',
  sessionSubtitle: 'Sélectionnez la session sur laquelle travailler.',
  loadingSessions: 'Chargement des sessions…',
  brandTagline: 'Espace client',
  brandHeadline: 'Votre espace client. Toute votre assurance, à portée de clic.',
  brandBody:
    'Contrats, sinistres, primes, flotte automobile : suivez l’ensemble de vos garanties depuis une interface unique, conçue pour les professionnels.',
  /** Footer line at the bottom of the brand panel. */
  brandFooter: (tenantName: string): string => `© ${tenantName} · Espace client`,
  usernameLabel: 'Identifiant',
  usernamePlaceholder: 'Entrez votre identifiant',
  passwordLabel: 'Mot de passe',
  passwordPlaceholder: 'Entrez votre mot de passe',
  veosLabel: 'VEOS',
  veosPlaceholder: 'Environnement VEOS',
  submit: 'Se connecter',
  sessionLabel: 'Session',
  sessionPlaceholder: 'Sélectionner une session…',
  sessionSubmit: 'Continuer',
} as const;

/** French messages of the login refusals (contract §2). */
export const LOGIN_ERROR_MESSAGES = {
  EXTRANET_RECETTE_REQUIRED:
    "Votre compte n'est pas autorisé à se connecter à l'extranet de recette.",
  VEOS_ENVIRONMENT_FORBIDDEN:
    "Vous n'êtes pas autorisé à vous connecter à cet environnement VEOS.",
  MODULE_ACCESS_DENIED: "Accès refusé : votre compte n'a pas accès à l'espace client.",
  forbidden: 'Accès refusé.',
  invalidCredentials: 'Identifiant ou mot de passe incorrect.',
  notFound: 'Espace client introuvable.',
  controlFailed: 'Le contrôle de connexion VEOS a échoué. Veuillez réessayer.',
  network: 'Connexion au serveur impossible. Vérifiez votre connexion réseau.',
  generic: 'La connexion a échoué. Veuillez réessayer.',
} as const;

/** 403 refusal codes with a dedicated message. */
const REFUSAL_CODES = new Set<string>([
  'EXTRANET_RECETTE_REQUIRED',
  'VEOS_ENVIRONMENT_FORBIDDEN',
  'MODULE_ACCESS_DENIED',
]);

// ---------------------------------------------------------------------------
// Error mapping + submission
// ---------------------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function readString(record: Record<string, unknown> | null, key: string): string | null {
  const value = record?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * French message of a failed login.
 *
 * @param status - HTTP status (`0` when the request never completed).
 * @param body   - Parsed error body (`{ message, code?, errors? }`), if any.
 */
export function resolveLoginErrorMessage(status: number, body: unknown): string {
  const payload = asRecord(body);
  const code = readString(payload, 'code');
  if (code !== null && REFUSAL_CODES.has(code)) {
    return LOGIN_ERROR_MESSAGES[code as keyof typeof LOGIN_ERROR_MESSAGES];
  }
  switch (status) {
    case 0:
      return LOGIN_ERROR_MESSAGES.network;
    case 401:
      return LOGIN_ERROR_MESSAGES.invalidCredentials;
    case 403:
      return LOGIN_ERROR_MESSAGES.forbidden;
    case 404:
      return LOGIN_ERROR_MESSAGES.notFound;
    case 502:
      return LOGIN_ERROR_MESSAGES.controlFailed;
    default:
      return readString(payload, 'message') ?? LOGIN_ERROR_MESSAGES.generic;
  }
}

/**
 * Error thrown by the login mutation. `data.message` is the French message
 * (what `EntityForm.Alert` shows); `data.errors` keeps the 422 field errors
 * so `mapFieldErrors` still lands them on the fields.
 */
export class LoginSubmitError extends Error {
  readonly status: number;
  readonly data: Record<string, unknown>;

  constructor(status: number, body: unknown) {
    const message = resolveLoginErrorMessage(status, body);
    super(message);
    this.name = 'LoginSubmitError';
    this.status = status;
    this.data = { ...(asRecord(body) ?? {}), message };
  }
}

/**
 * POST the credentials (plus `tenant_slug` from the URL and `language: fr`).
 * Resolves with the JSON body; rejects with {@link LoginSubmitError}.
 *
 * `fetch`, not the axios client: a wrong password answers 401, which must
 * surface as a form error rather than trigger the client's 401 reload.
 */
export async function submitLogin(
  payload: Record<string, unknown>,
  tenantSlug: string,
): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(LOGIN_ENDPOINT, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, tenant_slug: tenantSlug, language: 'fr' }),
    });
  } catch {
    throw new LoginSubmitError(0, null);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok) {
    throw new LoginSubmitError(response.status, body);
  }
  return body;
}

// ---------------------------------------------------------------------------
// Form configs
// ---------------------------------------------------------------------------

/**
 * Login form config. The « VEOS » select (`registry_slug`) is visible only
 * when the server says `selector_visible` and offers at least one
 * environment; it defaults to the server's default registry. Hidden, it is
 * not posted and the server picks its default for the deployment mode.
 */
export function buildLoginFormConfig(
  environments: ExtranetEnvironmentsPayload | null,
): EntityFormConfig {
  const items = (environments?.environments ?? []).map((env) => ({
    value: env.slug,
    label: env.label,
  }));
  const selectorVisible = Boolean(environments?.selector_visible) && items.length > 0;
  const defaultRegistry =
    environments?.default_registry_slug ??
    environments?.environments.find((env) => env.is_default)?.slug;

  return {
    id: 'extranet-login',
    endpoint: { erpEndpoint: LOGIN_ENDPOINT, method: 'POST' },
    fields: {
      username: {
        type: 'text',
        label: LOGIN_TEXT.usernameLabel,
        placeholder: LOGIN_TEXT.usernamePlaceholder,
        order: 1,
        options: { autocomplete: 'username' },
        validation: { required: true },
      },
      password: {
        type: 'password',
        label: LOGIN_TEXT.passwordLabel,
        placeholder: LOGIN_TEXT.passwordPlaceholder,
        order: 2,
        options: { autocomplete: 'current-password' },
        validation: { required: true },
      },
      registry_slug: {
        type: 'select',
        label: LOGIN_TEXT.veosLabel,
        placeholder: LOGIN_TEXT.veosPlaceholder,
        order: 3,
        visible: selectorVisible,
        ...(defaultRegistry ? { defaultValue: defaultRegistry } : {}),
        options: { items },
        validation: { required: selectorVisible },
      },
    },
    actions: {
      submit: { label: LOGIN_TEXT.submit, variant: 'primary', className: 'w-full' },
      cancel: { visible: false },
      reset: { visible: false },
    },
    workflow: { onError: { mapFieldErrors: true } },
    layout: { columns: 1, gap: 'md' },
  };
}

/** Session step config: one searchable select (thousands of sessions). */
export function buildSessionFormConfig(
  sessions: readonly ErpSession[],
  currentSessionId: number | null,
): EntityFormConfig {
  return {
    id: 'extranet-session',
    // Never posted: the session step's mutation switches the session locally.
    endpoint: { erpEndpoint: '', method: 'POST' },
    fields: {
      session: {
        type: 'select',
        label: LOGIN_TEXT.sessionLabel,
        placeholder: LOGIN_TEXT.sessionPlaceholder,
        order: 1,
        ...(currentSessionId === null ? {} : { defaultValue: String(currentSessionId) }),
        options: {
          items: sessions.map((s) => ({
            value: String(s.session),
            label: s.lib ?? String(s.session),
          })),
          searchable: true,
        },
        validation: { required: true },
      },
    },
    actions: {
      submit: { label: LOGIN_TEXT.sessionSubmit, variant: 'primary', className: 'w-full' },
      cancel: { visible: false },
      reset: { visible: false },
    },
    layout: { columns: 1, gap: 'md' },
  };
}

/** Display name of the tenant before login (no API carries it anonymously). */
export function tenantDisplayName(tenantSlug: string): string {
  const words = tenantSlug.split(/[-_]+/).filter((word) => word !== '');
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

/** React Query key of the environments list. */
export function environmentsQueryKey(tenantSlug: string, debug: boolean): readonly unknown[] {
  return ['extranet', 'auth-environments', tenantSlug, debug];
}
